/**
 * Prefetch de chunks + dados das páginas (hover no menu).
 * Mantém o mapa alinhado com App.js lazy imports.
 */
import api from "@/lib/api";
import queryClient from "@/lib/queryClient";
import { resolvePeriodRange, toApiParams } from "@/lib/period";

export const PAGE_LOADERS = {
  "/": () => import("@/pages/Dashboard"),
  "/financeiro": () => import("@/pages/Financeiro"),
  "/fluxo-caixa": () => import("@/pages/FluxoCaixa"),
  "/fechamento": () => import("@/pages/Fechamento"),
  "/historico": () => import("@/pages/Historico"),
  "/relatorios": () => import("@/pages/Relatorios"),
  "/comissoes": () => import("@/pages/Comissoes"),
  "/leads": () => import("@/pages/Leads"),
  "/tarefas": () => import("@/pages/Tarefas"),
  "/clientes": () => import("@/pages/Clientes"),
  "/agendamentos": () => import("@/pages/Agendamentos"),
  "/procedimentos": () => import("@/pages/Procedimentos"),
  "/pacotes": () => import("@/pages/Pacotes"),
  "/estoque": () => import("@/pages/Estoque"),
  "/trocas": () => import("@/pages/Trocas"),
  "/comunicacao": () => import("@/pages/Comunicacao"),
  "/whatsapp": () => import("@/pages/WhatsApp"),
  "/whatsapp-config": () => import("@/pages/WhatsAppConfig"),
  "/equipe": () => import("@/pages/Equipe"),
  "/ajuda": () => import("@/pages/Ajuda"),
};

function defaultDashParams() {
  const range = resolvePeriodRange("mes");
  return toApiParams({ from: range.from, to: range.to });
}

/** Queries por rota — aquecem React Query antes do clique. */
const PAGE_QUERIES = {
  "/": () => {
    const params = defaultDashParams();
    return [
      [["professionals"], () => api.get("/professionals").then((r) => r.data)],
      [["procedures"], () => api.get("/procedures").then((r) => r.data)],
      [["tarefas-resumo"], () => api.get("/tarefas/resumo").then((r) => r.data)],
      [["dashboard", params], () => api.get("/dashboard", { params }).then((r) => r.data)],
    ];
  },
  "/clientes": () => [
    [["clientes"], () => api.get("/clientes").then((r) => r.data)],
  ],
  "/agendamentos": () => [
    [["appointments"], () => api.get("/appointments").then((r) => r.data)],
    [["professionals"], () => api.get("/professionals").then((r) => r.data)],
    [["procedures"], () => api.get("/procedures").then((r) => r.data)],
    [["clientes"], () => api.get("/clientes").then((r) => r.data)],
  ],
  "/financeiro": () => [
    [["transactions"], () => api.get("/transactions").then((r) => r.data)],
  ],
  "/leads": () => [
    [["leads"], () => api.get("/leads").then((r) => r.data)],
  ],
  "/tarefas": () => [
    [["tarefas"], () => api.get("/tarefas").then((r) => r.data)],
    [["clientes"], () => api.get("/clientes").then((r) => r.data)],
    [["professionals"], () => api.get("/professionals").then((r) => r.data)],
  ],
  "/procedimentos": () => [
    [["procedures"], () => api.get("/procedures").then((r) => r.data)],
  ],
  "/pacotes": () => [
    [["pacotes"], () => api.get("/pacotes").then((r) => r.data)],
    [["clientes"], () => api.get("/clientes").then((r) => r.data)],
    [["procedures"], () => api.get("/procedures").then((r) => r.data)],
  ],
  "/estoque": () => [
    [["products"], () => api.get("/products").then((r) => r.data)],
  ],
  "/equipe": () => [
    [["staff"], () => api.get("/staff").then((r) => r.data)],
  ],
  "/trocas": () => [
    [["trocas"], () => api.get("/trocas").then((r) => r.data)],
    [["clientes"], () => api.get("/clientes").then((r) => r.data)],
  ],
  "/comunicacao": () => [
    [["templates"], () => api.get("/mensagem-templates").then((r) => r.data)],
  ],
  "/fechamento": () => [
    [["fechamentos"], () => api.get("/fechamentos").then((r) => r.data)],
  ],
  "/historico": () => [
    [["historico-feed", { limit: 100 }], () => api.get("/historico", { params: { limit: 100 } }).then((r) => r.data)],
  ],
  "/comissoes": () => [
    [["professionals"], () => api.get("/professionals").then((r) => r.data)],
  ],
  "/relatorios": () => [
    [["professionals"], () => api.get("/professionals").then((r) => r.data)],
    [["procedures"], () => api.get("/procedures").then((r) => r.data)],
  ],
};

const warmedChunks = new Set();
const warmedData = new Set();

function prefetchQueries(path) {
  if (warmedData.has(path)) return;
  const factory = PAGE_QUERIES[path];
  if (!factory) return;
  const specs = factory();
  if (!specs?.length) return;
  warmedData.add(path);
  Promise.all(
    specs.map(([queryKey, queryFn]) =>
      queryClient.prefetchQuery({ queryKey, queryFn }),
    ),
  ).catch(() => {
    warmedData.delete(path);
  });
}

export function prefetchPage(path) {
  const base = path.split("?")[0];
  if (base.startsWith("/clientes/") && base !== "/clientes") {
    if (!warmedChunks.has("ClienteDetalhe")) {
      warmedChunks.add("ClienteDetalhe");
      import("@/pages/ClienteDetalhe").catch(() => warmedChunks.delete("ClienteDetalhe"));
    }
  }
  const key = base.startsWith("/clientes/") ? "/clientes" : base;
  const load = PAGE_LOADERS[key];
  if (load && !warmedChunks.has(key)) {
    warmedChunks.add(key);
    load().catch(() => warmedChunks.delete(key));
  }
  prefetchQueries(key);
}
